import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getVideoAdmin,
  updateVideoAdmin,
  upsertLocation,
  deleteLocation,
  processVideoContent,
  retranscribeVideo,
  getVideoNav,
  getVideoViewHistory,
  geolocateVideoOne,
  refreshVideoStats,
} from "@/lib/admin.functions";
import { formatCompact, formatDate, formatRelative, formatDuration, translateCountry, countryFlagUrl, cleanTitle } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { MapView, type MapPin as MapPinT } from "@/components/map-view";
import { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Youtube,
  Save,
  X,
  EyeOff,
  Eye,
  MapPin,
  Sparkles,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

// ---- Cost estimation (mirrors admin.youtubers.$id) ------------------------
const MODEL_PRICING: Record<string, { in: number; out: number }> = {
  "gemini-2.5-flash": { in: 0.3, out: 2.5 },
  "gemini-2.5-flash-lite": { in: 0.1, out: 0.4 },
  "gemini-2.5-pro": { in: 1.25, out: 10 },
  "gemini-2.0-flash": { in: 0.1, out: 0.4 },
  "gemini-2.0-flash-lite": { in: 0.075, out: 0.3 },
  "gemini-1.5-pro": { in: 1.25, out: 5 },
  "gemini-1.5-flash": { in: 0.075, out: 0.3 },
  "gpt-4o-mini": { in: 0.15, out: 0.6 },
  "gpt-4o": { in: 2.5, out: 10 },
  "gpt-4.1-mini": { in: 0.4, out: 1.6 },
  "gpt-5-mini": { in: 0.25, out: 2 },
  "gpt-5": { in: 1.25, out: 10 },
  "gpt-5-nano": { in: 0.05, out: 0.4 },
};
function normalizeModel(m: string): string {
  return (m || "")
    .toLowerCase()
    .replace(/^[a-z0-9_-]+\//, "")
    .replace(/-(latest|exp)(-\d+)?$/g, "")
    .replace(/-preview.*$/g, "")
    .trim();
}
function estimateCostUsd(model: string, prompt: number, completion: number): number {
  const key = normalizeModel(model);
  let p = MODEL_PRICING[key];
  if (!p) {
    const match = Object.keys(MODEL_PRICING)
      .filter((k) => key.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    if (match) p = MODEL_PRICING[match];
  }
  if (!p) return 0;
  return (prompt * p.in + completion * p.out) / 1_000_000;
}
function fmtCost(n: number): string {
  if (n <= 0) return "$0";
  if (n < 0.0001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

type JobItem = { title: string; ok: boolean; error: string | null; detail?: string };
type JobUsage = { prompt: number; completion: number; costUsd: number };
type JobLog = {
  id: number;
  kind: string;
  startedAt: number;
  running: boolean;
  items: JobItem[];
  summary?: string;
  usage: JobUsage;
};
export type JobLogger = {
  start: (kind: string) => number;
  appendItems: (id: number, items: JobItem[]) => void;
  addUsage: (id: number, u: any) => void;
  finish: (id: number, summary: string) => void;
};

export const Route = createFileRoute("/_authenticated/admin/videos/$id")({
  staticData: { sitemap: false },
  component: AdminVideoDetail,
});

function AdminVideoDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { i18n } = useTranslation();

  const getFn = useServerFn(getVideoAdmin);
  const { data: v, isLoading } = useQuery({
    queryKey: ["admin", "video", id],
    queryFn: () => getFn({ data: { id } }),
  });

  const navFn = useServerFn(getVideoNav);
  const { data: nav } = useQuery({
    queryKey: ["admin", "video-nav", id],
    queryFn: () => navFn({ data: { id } }),
  });


  const updFn = useServerFn(updateVideoAdmin);
  const updMut = useMutation({
    mutationFn: (patch: any) => updFn({ data: { id, patch } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "video", id] });
      qc.invalidateQueries({ queryKey: ["admin", "videos"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [form, setForm] = useState({
    title: "",
    description: "",
    description_clean: "",
    transcript_summary: "",
  });

  useEffect(() => {
    if (v) {
      setForm({
        title: v.title ?? "",
        description: v.description ?? "",
        description_clean: v.description_clean ?? "",
        transcript_summary: v.transcript_summary ?? "",
      });
    }
  }, [v]);

  const [tab, setTab] = useState<"location" | "data" | "views">("location");

  // Persistent job log (matches the panel on the youtuber admin page).
  const [jobs, setJobs] = useState<JobLog[]>([]);
  const jobIdRef = useRef(0);
  // Clear the job log when the video changes so entries don't leak across pages.
  useEffect(() => {
    setJobs([]);
    jobIdRef.current = 0;
  }, [id]);
  const logger: JobLogger = useMemo(
    () => ({
      start: (kind: string) => {
        const jid = ++jobIdRef.current;
        setJobs((prev) =>
          [
            {
              id: jid,
              kind,
              startedAt: Date.now(),
              running: true,
              items: [],
              usage: { prompt: 0, completion: 0, costUsd: 0 },
            },
            ...prev,
          ].slice(0, 5),
        );
        return jid;
      },
      appendItems: (jid, items) =>
        setJobs((prev) => prev.map((j) => (j.id === jid ? { ...j, items: [...j.items, ...items] } : j))),
      addUsage: (jid, u) => {
        if (!u) return;
        let prompt = Number(u.prompt_tokens ?? 0) || 0;
        const completion = Number(u.completion_tokens ?? 0) || 0;
        const total = Number(u.total_tokens ?? 0) || 0;
        if (prompt === 0 && completion === 0 && total > 0) prompt = total;
        const cost = estimateCostUsd(u.model ?? "", prompt, completion);
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jid
              ? {
                  ...j,
                  usage: {
                    prompt: j.usage.prompt + prompt,
                    completion: j.usage.completion + completion,
                    costUsd: j.usage.costUsd + cost,
                  },
                }
              : j,
          ),
        );
      },
      finish: (jid, summary) =>
        setJobs((prev) => prev.map((j) => (j.id === jid ? { ...j, running: false, summary } : j))),
    }),
    [],
  );
  const dismissJob = (jid: number) => setJobs((prev) => prev.filter((j) => j.id !== jid));

  const processFn = useServerFn(processVideoContent);
  const processMut = useMutation({
    mutationFn: async () => {
      const jid = logger.start("PROCESS CONTENT");
      try {
        const res: any = await processFn({ data: { id } });
        if (Array.isArray(res?.usages)) res.usages.forEach((u: any) => logger.addUsage(jid, u));
        else logger.addUsage(jid, res?.usage);
        const ok = res?.ok !== false;
        logger.appendItems(jid, [{
          title: v?.title ?? id,
          ok,
          error: ok ? null : res?.error ?? "Failed",
        }]);
        logger.finish(jid, ok ? "Processed" : "Failed");
        return res;
      } catch (e: any) {
        logger.appendItems(jid, [{ title: v?.title ?? id, ok: false, error: e?.message ?? String(e) }]);
        logger.finish(jid, "Failed");
        return { ok: false, error: e?.message ?? String(e) };
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "video", id] });
    },
  });


  if (isLoading || !v) {
    return <div className="p-10 text-ink/50">Loading…</div>;
  }

  const pins: MapPinT[] = (v.video_locations ?? [])
    .filter((l: any) => typeof l.latitude === "number" && typeof l.longitude === "number")
    .map((l: any) => ({
      id: l.id,
      lat: l.latitude,
      lng: l.longitude,
      label: l.name ?? l.city ?? l.country_name ?? "",
      color: l.is_primary ? "hsl(35 85% 55%)" : "hsl(35 45% 55%)",
    }));

  const yt = v.youtubers;
  const dirty =
    form.title !== (v.title ?? "") ||
    form.description !== (v.description ?? "") ||
    form.description_clean !== (v.description_clean ?? "") ||
    form.transcript_summary !== (v.transcript_summary ?? "");

  return (
    <div className={`p-10 max-w-6xl min-h-screen ${v.hidden ? "bg-destructive/5" : ""}`}>
      {/* Persistent job log */}
      {jobs.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 w-96 max-w-[calc(100vw-2rem)] space-y-2">
          {jobs.map((j) => {
            const okCount = j.items.filter((i) => i.ok).length;
            const failCount = j.items.filter((i) => !i.ok).length;
            return (
              <div key={j.id} className="bg-paper ring-hairline shadow-lg text-sm">
                <div className="flex items-center justify-between px-3 py-2 border-b border-ink/10 bg-secondary/50">
                  <div className="flex items-center gap-2 min-w-0">
                    {j.running ? (
                      <Loader2 className="size-3.5 animate-spin text-amber flex-shrink-0" />
                    ) : failCount > 0 ? (
                      <AlertCircle className="size-3.5 text-red-600 flex-shrink-0" />
                    ) : (
                      <CheckCircle2 className="size-3.5 text-green-700 flex-shrink-0" />
                    )}
                    <span className="text-mono-caps text-xs truncate">{j.kind}</span>
                    <span className="text-xs text-ink/50">
                      {okCount}✓ {failCount > 0 ? `${failCount}✗` : ""}
                    </span>
                  </div>
                  <button
                    onClick={() => dismissJob(j.id)}
                    className="text-ink/40 hover:text-ink flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-ink/5">
                  {j.items.length === 0 && j.running && (
                    <div className="px-3 py-2 text-xs text-ink/50">Working…</div>
                  )}
                  {j.items.map((it, idx) => (
                    <div key={idx} className="px-3 py-1.5 flex items-start gap-2">
                      {it.ok ? (
                        <CheckCircle2 className="size-3 text-green-700 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="size-3 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs truncate" title={cleanTitle(it.title)}>{cleanTitle(it.title)}</div>
                        {(it.error || it.detail) && (
                          <div className={`text-[11px] ${it.ok ? "text-ink/50" : "text-red-700"}`}>
                            {it.error ?? it.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {(j.usage.prompt > 0 || j.usage.completion > 0) && (
                  <div className="px-3 py-1.5 border-t border-ink/10 text-[11px] text-ink/60 font-mono flex items-center gap-2">
                    <span>tok: {j.usage.prompt.toLocaleString()} in · {j.usage.completion.toLocaleString()} out</span>
                    <span className="ml-auto text-ink/70">≈ {fmtCost(j.usage.costUsd)}</span>
                  </div>
                )}
                {j.summary && !j.running && (
                  <div className="px-3 py-1.5 border-t border-ink/10 text-xs text-ink/60">{j.summary}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Header (thumb + back link on same row) */}
      <div className="grid md:grid-cols-[280px_1fr] gap-6 mb-8 items-start relative">

        {/* Prev / Next arrows (top-right) */}
        <div className="absolute top-0 right-0 flex items-center gap-1 z-10">
          {nav?.prevId ? (
            <Link
              to="/admin/videos/$id"
              params={{ id: nav.prevId }}
              aria-label="Previous (older) video"
              title="Previous (older) video"
              className="ring-hairline w-8 h-8 grid place-items-center bg-paper hover:bg-secondary transition-colors"
            >
              <ChevronLeft className="size-4" />
            </Link>
          ) : (
            <span className="ring-hairline w-8 h-8 grid place-items-center bg-paper opacity-30">
              <ChevronLeft className="size-4" />
            </span>
          )}
          {nav?.nextId ? (
            <Link
              to="/admin/videos/$id"
              params={{ id: nav.nextId }}
              aria-label="Next (newer) video"
              title="Next (newer) video"
              className="ring-hairline w-8 h-8 grid place-items-center bg-paper hover:bg-secondary transition-colors"
            >
              <ChevronRight className="size-4" />
            </Link>
          ) : (
            <span className="ring-hairline w-8 h-8 grid place-items-center bg-paper opacity-30">
              <ChevronRight className="size-4" />
            </span>
          )}
        </div>

        <div className="aspect-video bg-secondary overflow-hidden ring-hairline">
          {v.thumbnail_url && (
            <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
          )}
        </div>
        <div className="min-w-0">
          <Link
            to="/admin/youtubers/$id"
            params={{ id: yt?.id ?? "" }}
            className="inline-flex items-center gap-1.5 text-mono-caps text-ink/50 hover:text-ink mb-2"
          >
            <ArrowLeft className="size-3.5" /> Back to {yt?.name ?? "channel"}
          </Link>
          <p className="text-mono-caps text-amber mb-1">Video</p>
          <h1 className="text-2xl leading-tight font-serif">{cleanTitle(v.title)}</h1>
          {(() => {
            const primary =
              (v.video_locations ?? []).find((l: any) => l.is_primary) ??
              (v.video_locations ?? [])[0];
            if (!primary?.country_code) return null;
            const flag = countryFlagUrl(primary.country_code, 40);
            return (
              <div className="inline-flex items-center gap-2 mt-2 text-sm text-ink/70">
                {flag && (
                  <img
                    src={flag}
                    alt=""
                    aria-hidden
                    className="w-6 h-[18px] rounded-[2px] ring-1 ring-ink/10"
                  />
                )}
                <span>
                  {translateCountry(primary.country_code, primary.country_name, i18n.language)}
                  {primary.city && <span className="text-ink/50"> · {primary.city}</span>}
                </span>
              </div>
            );
          })()}
          <div className="text-mono-caps text-ink/50 mt-2 text-xs">
            {formatDate(v.published_at, i18n.language)} · {formatCompact(v.view_count)} views ·{" "}
            {formatCompact(v.like_count)} likes · {formatDuration(v.duration_seconds)}
            <RefreshStatsButton videoId={v.id} />
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs flex-wrap">
            <a
              href={`https://www.youtube.com/watch?v=${v.youtube_video_id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 ring-hairline px-2.5 py-1 hover:bg-secondary"
            >
              <Youtube className="size-3" /> Watch on YouTube
            </a>
            {yt && (
              <Link
                to="/y/$handle"
                params={{ handle: yt.handle }}
                target="_blank"
                className="inline-flex items-center gap-1 ring-hairline px-2.5 py-1 hover:bg-secondary"
              >
                <ExternalLink className="size-3" /> {yt.name}
              </Link>
            )}
            <button
              onClick={() => updMut.mutate({ hidden: !v.hidden })}
              className="inline-flex items-center gap-1 ring-hairline px-2.5 py-1 hover:bg-secondary"
            >
              {v.hidden ? <><Eye className="size-3" /> Unhide</> : <><EyeOff className="size-3" /> Hide</>}
            </button>
          </div>
        </div>
      </div>


      {/* Tabs */}
      {(() => {
        const hasGeo = v.geolocated && (v.video_locations?.length ?? 0) > 0;
        const hasTranscript = !!(v.transcript && String(v.transcript).trim());
        const hasSummary = !!(v.transcript_summary && String(v.transcript_summary).trim());
        const geoDescriptionOnly = hasGeo && v.geo_source === "description";
        const locationSeverity: "none" | "warn" | "error" = !hasGeo
          ? "error"
          : geoDescriptionOnly
          ? "warn"
          : "none";
        const dataAlert = !hasTranscript || !hasSummary;
        const TabBtn = ({
          id: tid,
          label,
          severity = "none",
          alert,
          alertTitle,
        }: {
          id: "location" | "data" | "views";
          label: string;
          severity?: "none" | "warn" | "error";
          alert?: boolean;
          alertTitle?: string;
        }) => {
          const showIcon = severity !== "none" || alert;
          const iconClass =
            severity === "warn"
              ? "text-amber-500"
              : severity === "error" || alert
              ? "text-red-600"
              : "";
          return (
            <button
              onClick={() => setTab(tid)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${
                tab === tid
                  ? "border-amber text-ink"
                  : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {label}
              {showIcon && (
                <span title={alertTitle} aria-label={alertTitle}>
                  <AlertTriangle className={`size-3.5 ${iconClass}`} />
                </span>
              )}
            </button>
          );
        };
        return (
          <>
            <div className="flex items-center gap-1 border-b border-ink/10 mb-6">
              <TabBtn
                id="location"
                label="Location"
                severity={locationSeverity}
                alertTitle={
                  locationSeverity === "error"
                    ? "Video is not geolocated"
                    : locationSeverity === "warn"
                    ? "Geolocated from description only"
                    : undefined
                }
              />
              <TabBtn
                id="data"
                label="Data"
                alert={dataAlert}
                alertTitle={
                  !hasTranscript
                    ? "Missing transcript"
                    : !hasSummary
                    ? "Missing summary"
                    : undefined
                }
              />
              <TabBtn id="views" label="Views" />
            </div>

            {tab === "location" && (
              <>
                <div className="mb-3">
                  <ReGeolocateButton videoId={v.id} videoTitle={v.title ?? ""} hasGeo={pins.length > 0} logger={logger} />
                </div>
                <div className="ring-hairline bg-paper mb-8">
                  <div className="text-mono-caps text-ink/50 px-4 pt-3">
                    Locations map ({pins.length})
                  </div>
                  <div className="h-80 mt-2">
                    {pins.length > 0 ? (
                      <MapView pins={pins} fitToPins initialZoom={2} className="w-full h-full" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-ink/40 text-sm">
                        No coordinates yet
                      </div>
                    )}
                  </div>
                </div>
                <LocationsEditor v={v} />
              </>
            )}


            {tab === "data" && (
              <div className="ring-hairline bg-paper p-5 mb-8">
                <div className="text-mono-caps text-ink/50 mb-3">Video data</div>
                <div className="space-y-4">
                  <div>
                    <label className="text-mono-caps text-ink/50 text-xs">Title</label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full ring-hairline bg-paper px-3 py-2 text-sm mt-1"
                    />
                    {v.title_original ? (
                      <div className="mt-1 text-xs text-ink/50">
                        Original title: <span dir="auto">{v.title_original}</span>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className="text-mono-caps text-ink/50 text-xs">Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full ring-hairline bg-paper px-3 py-2 text-sm mt-1 font-mono"
                      rows={6}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-mono-caps text-ink/50 text-xs">Cleaned description (shown publicly)</label>
                      <button
                        onClick={() => processMut.mutate()}
                        disabled={processMut.isPending}
                        className="text-mono-caps text-xs text-amber hover:text-amber-deep disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <Sparkles className="size-3" /> {processMut.isPending ? "Processing…" : v.content_processed_at ? "Regenerate" : "Generate"}
                      </button>
                    </div>
                    <textarea
                      value={form.description_clean}
                      onChange={(e) => setForm({ ...form, description_clean: e.target.value })}
                      placeholder="Non-promotional summary of the description…"
                      className="w-full ring-hairline bg-paper px-3 py-2 text-sm mt-1"
                      rows={4}
                    />
                  </div>

                  <TranscriptPanel
                    videoId={id}
                    youtubeVideoId={v.youtube_video_id}
                    storedTranscript={v.transcript ?? ""}
                    originalTranscript={(v as any).transcript_original ?? null}
                    source={v.transcript_source ?? null}
                    transcriptError={v.transcript_error ?? null}
                    fetchedAt={v.transcript_fetched_at ?? null}
                    logger={logger}
                  />

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-mono-caps text-ink/50 text-xs">Transcript summary (shown publicly)</label>
                      <button
                        onClick={() => processMut.mutate()}
                        disabled={processMut.isPending}
                        className="text-mono-caps text-xs text-amber hover:text-amber-deep disabled:opacity-50 inline-flex items-center gap-1"
                        title="Regenerate summary and cleaned description from the current transcript"
                      >
                        <Sparkles className="size-3" /> {processMut.isPending ? "Processing…" : v.transcript_summary ? "Regenerate" : "Generate"}
                      </button>
                    </div>
                    <textarea
                      value={form.transcript_summary}
                      onChange={(e) => setForm({ ...form, transcript_summary: e.target.value })}
                      placeholder="Short summary of the video's content…"
                      className="w-full ring-hairline bg-paper px-3 py-2 text-sm mt-1"
                      rows={4}
                    />
                    {v.content_processed_at && (
                      <div className="text-mono-caps text-ink/40 text-[10px] mt-1">
                        Processed {formatRelative(v.content_processed_at)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      disabled={!dirty || updMut.isPending}
                      onClick={() =>
                        updMut.mutate({
                          title: form.title,
                          description: form.description,
                          // Store blank fields as NULL, not "", so the Channels
                          // overview counts (which use IS NULL) stay consistent
                          // with the "missing" filters (which treat "" as missing).
                          description_clean: form.description_clean.trim() || null,
                          transcript_summary: form.transcript_summary.trim() || null,
                        })
                      }
                      className="bg-ink text-paper px-4 py-2 text-sm hover:bg-amber transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <Save className="size-4" /> Save changes
                    </button>
                    {dirty && (
                      <button
                        onClick={() =>
                          setForm({
                            title: v.title ?? "",
                            description: v.description ?? "",
                            description_clean: v.description_clean ?? "",
                            transcript_summary: v.transcript_summary ?? "",
                          })
                        }
                        className="ring-hairline px-4 py-2 text-sm hover:bg-secondary"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "views" && <ViewHistoryPanel videoId={id} />}
          </>
        );
      })()}


      <div className="mt-10 pt-6 border-t border-ink/10">
        <DeleteVideoBlock videoId={v.id} youtuberId={yt?.id} onDone={() => navigate({ to: "/admin/youtubers/$id", params: { id: yt?.id ?? "" } })} />
      </div>
    </div>
  );
}


function LocationsEditor({ v }: { v: any }) {
  const qc = useQueryClient();
  const upsertFn = useServerFn(upsertLocation);
  const delFn = useServerFn(deleteLocation);
  const updFn = useServerFn(updateVideoAdmin);

  const upsertMut = useMutation({
    mutationFn: (input: any) => upsertFn({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "video", v.id] }),
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "video", v.id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const [newLoc, setNewLoc] = useState({
    name: "",
    country_code: "",
    city: "",
    latitude: "",
    longitude: "",
    is_primary: false,
  });

  const { i18n } = useTranslation();
  const countryOptions = useMemo(() => {
    let codes: string[] = [];
    try {
      // @ts-expect-error - supportedValuesOf may not be typed
      codes = (Intl.supportedValuesOf?.("region") ?? []).filter((c: string) => /^[A-Z]{2}$/.test(c));
    } catch {
      codes = [];
    }
    if (codes.length === 0) {
      codes = "AD AE AF AG AL AM AO AR AT AU AZ BA BB BD BE BF BG BH BI BJ BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GH GM GN GQ GR GT GW GY HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KI KM KN KP KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PT PW PY QA RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TV TZ UA UG US UY UZ VA VC VE VN VU WS YE ZA ZM ZW".split(" ");
    }
    // Territories/regions that some runtimes omit but we still need as destinations
    const extras = "TW HK MO PS PR GL FO GI GU GP MQ RE NC PF AW CW SX BM KY VG VI TC AI MS FK GF YT IM JE GG AX SJ EH XK CK NU TK WF BL MF PM SH".split(" ");
    const all = Array.from(new Set([...codes, ...extras]));
    return all
      .map((code) => ({ code, name: translateCountry(code, code, i18n.language) }))
      .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [i18n.language]);

  return (
    <div className="ring-hairline bg-paper p-5">
      <div className="text-mono-caps text-ink/50 mb-3">
        Locations ({v.video_locations.length})
      </div>
      <div className="space-y-2 mb-4">
        {v.video_locations.map((l: any) => (
          <LocationRow
            key={l.id}
            loc={l}
            countryOptions={countryOptions}
            onSave={(patch) => upsertMut.mutate({ ...l, ...patch })}
            onDelete={() => delMut.mutate(l.id)}
            onMakePrimary={() => upsertMut.mutate({ ...l, is_primary: true })}
            saving={upsertMut.isPending}
          />
        ))}
        {v.video_locations.length === 0 && (
          <p className="text-ink/40 text-sm">No locations yet.</p>
        )}
      </div>

      <div className="bg-secondary/40 p-3 ring-hairline">
        <div className="text-mono-caps text-ink/50 mb-2 flex items-center gap-1.5">
          <MapPin className="size-3.5" /> Add manual location
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
          <input placeholder="Name" value={newLoc.name} onChange={(e) => setNewLoc({ ...newLoc, name: e.target.value })} className="ring-hairline px-2 py-1 text-sm bg-paper" />
          <input placeholder="City" value={newLoc.city} onChange={(e) => setNewLoc({ ...newLoc, city: e.target.value })} className="ring-hairline px-2 py-1 text-sm bg-paper" />
          <div className="flex items-center gap-1.5 ring-hairline bg-paper px-2">
            {newLoc.country_code ? (
              <img
                src={countryFlagUrl(newLoc.country_code, 20) ?? ""}
                alt=""
                aria-hidden
                className="w-5 h-[15px] rounded-[2px] ring-1 ring-ink/10 flex-shrink-0"
              />
            ) : (
              <span className="w-5 h-[15px] bg-ink/5 rounded-[2px] flex-shrink-0" />
            )}
            <select
              value={newLoc.country_code}
              onChange={(e) => setNewLoc({ ...newLoc, country_code: e.target.value })}
              className="flex-1 bg-transparent py-1 text-sm focus:outline-none"
            >
              <option value="">Country…</option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <input placeholder="Latitude" value={newLoc.latitude} onChange={(e) => setNewLoc({ ...newLoc, latitude: e.target.value })} className="ring-hairline px-2 py-1 text-sm bg-paper" />
          <input placeholder="Longitude" value={newLoc.longitude} onChange={(e) => setNewLoc({ ...newLoc, longitude: e.target.value })} className="ring-hairline px-2 py-1 text-sm bg-paper" />
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={newLoc.is_primary} onChange={(e) => setNewLoc({ ...newLoc, is_primary: e.target.checked })} />
            Primary
          </label>
          <button
            onClick={async () => {
              const lat = parseFloat(newLoc.latitude);
              const lng = parseFloat(newLoc.longitude);
              if (isNaN(lat) || isNaN(lng)) {
                toast.error("Latitude and longitude required");
                return;
              }
              const cc = newLoc.country_code || null;
              const cname = cc ? translateCountry(cc, cc, "en") : null;
              await upsertMut.mutateAsync({
                video_id: v.id,
                name: newLoc.name || null,
                city: newLoc.city || null,
                country_name: cname,
                country_code: cc,
                latitude: lat,
                longitude: lng,
                is_primary: newLoc.is_primary || v.video_locations.length === 0,
              });
              await updFn({ data: { id: v.id, patch: { geolocated: true } } });
              qc.invalidateQueries({ queryKey: ["admin", "video", v.id] });
              setNewLoc({ name: "", country_code: "", city: "", latitude: "", longitude: "", is_primary: false });
            }}
            className="bg-ink text-paper text-xs px-3 py-1.5 hover:bg-amber transition-colors flex items-center gap-1.5"
          >
            <Save className="size-3.5" /> Add
          </button>
        </div>
      </div>
    </div>
  );
}


type CountryOpt = { code: string; name: string };

function LocationRow({
  loc,
  countryOptions,
  onSave,
  onDelete,
  onMakePrimary,
  saving,
}: {
  loc: any;
  countryOptions: CountryOpt[];
  onSave: (patch: any) => void;
  onDelete: () => void;
  onMakePrimary: () => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    name: loc.name ?? "",
    city: loc.city ?? "",
    region: loc.region ?? "",
    country_code: loc.country_code ?? "",
    latitude: loc.latitude == null ? "" : String(loc.latitude),
    longitude: loc.longitude == null ? "" : String(loc.longitude),
  });
  useEffect(() => {
    setF({
      name: loc.name ?? "",
      city: loc.city ?? "",
      region: loc.region ?? "",
      country_code: loc.country_code ?? "",
      latitude: loc.latitude == null ? "" : String(loc.latitude),
      longitude: loc.longitude == null ? "" : String(loc.longitude),
    });
  }, [loc.id, loc.name, loc.city, loc.region, loc.country_code, loc.latitude, loc.longitude]);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 text-sm bg-secondary/40 p-2 ring-hairline flex-wrap">
        {loc.is_primary && <span className="text-mono-caps text-amber">PRIMARY</span>}
        {loc.country_code && (
          <img
            src={countryFlagUrl(loc.country_code, 20) ?? ""}
            alt=""
            aria-hidden
            className="w-5 h-[15px] rounded-[2px] ring-1 ring-ink/10 flex-shrink-0"
          />
        )}
        <span className="font-medium">{loc.name ?? loc.city ?? loc.country_name}</span>
        <span className="text-ink/50">
          {[loc.city, loc.region, loc.country_name].filter(Boolean).join(", ")}
        </span>
        <span className="font-mono text-xs text-ink/40">
          {loc.latitude?.toFixed(3)}, {loc.longitude?.toFixed(3)}
        </span>
        <span className="text-mono-caps text-ink/40 ml-auto">{loc.source}</span>
        {!loc.is_primary && (
          <button onClick={onMakePrimary} className="text-xs text-amber hover:underline">
            Make primary
          </button>
        )}
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-ink/60 hover:text-ink hover:underline"
        >
          Edit
        </button>
        <button onClick={onDelete} className="text-destructive hover:text-destructive/70" title="Delete">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-secondary/40 p-3 ring-hairline">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <input
          placeholder="Name"
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
          className="ring-hairline px-2 py-1 text-sm bg-paper"
        />
        <input
          placeholder="City"
          value={f.city}
          onChange={(e) => setF({ ...f, city: e.target.value })}
          className="ring-hairline px-2 py-1 text-sm bg-paper"
        />
        <input
          placeholder="Region"
          value={f.region}
          onChange={(e) => setF({ ...f, region: e.target.value })}
          className="ring-hairline px-2 py-1 text-sm bg-paper"
        />
        <div className="flex items-center gap-1.5 ring-hairline bg-paper px-2">
          {f.country_code ? (
            <img
              src={countryFlagUrl(f.country_code, 20) ?? ""}
              alt=""
              aria-hidden
              className="w-5 h-[15px] rounded-[2px] ring-1 ring-ink/10 flex-shrink-0"
            />
          ) : (
            <span className="w-5 h-[15px] bg-ink/5 rounded-[2px] flex-shrink-0" />
          )}
          <select
            value={f.country_code}
            onChange={(e) => setF({ ...f, country_code: e.target.value })}
            className="flex-1 bg-transparent py-1 text-sm focus:outline-none"
          >
            <option value="">Country…</option>
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
        <input
          placeholder="Latitude"
          value={f.latitude}
          onChange={(e) => setF({ ...f, latitude: e.target.value })}
          className="ring-hairline px-2 py-1 text-sm bg-paper font-mono"
        />
        <input
          placeholder="Longitude"
          value={f.longitude}
          onChange={(e) => setF({ ...f, longitude: e.target.value })}
          className="ring-hairline px-2 py-1 text-sm bg-paper font-mono"
        />
      </div>
      <div className="flex gap-2 mt-2">
        <button
          disabled={saving}
          onClick={() => {
            const lat = parseFloat(f.latitude);
            const lng = parseFloat(f.longitude);
            if (isNaN(lat) || isNaN(lng)) {
              toast.error("Latitude and longitude required");
              return;
            }
            const cc = f.country_code || null;
            onSave({
              name: f.name || null,
              city: f.city || null,
              region: f.region || null,
              country_code: cc,
              country_name: cc ? translateCountry(cc, cc, "en") : null,
              latitude: lat,
              longitude: lng,
            });
            setEditing(false);
          }}
          className="bg-ink text-paper text-xs px-3 py-1.5 hover:bg-amber transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Save className="size-3.5" /> Save
        </button>
        <button
          onClick={() => setEditing(false)}
          className="ring-hairline px-3 py-1.5 text-xs hover:bg-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


function ReGeolocateButton({
  videoId,
  videoTitle,
  hasGeo,
  logger,
}: {
  videoId: string;
  videoTitle: string;
  hasGeo: boolean;
  logger: JobLogger;
}) {
  const qc = useQueryClient();
  const geoFn = useServerFn(geolocateVideoOne);
  const mut = useMutation({
    mutationFn: async () => {
      const jid = logger.start(hasGeo ? "RE-GEOLOCATE" : "GEOLOCATE");
      try {
        const r: any = await geoFn({ data: { id: videoId, replace: hasGeo } });
        if (r?.usage) logger.addUsage(jid, r.usage);
        const inserted = r?.inserted ?? 0;
        const ok = inserted > 0;
        logger.appendItems(jid, [
          {
            title: videoTitle || videoId,
            ok,
            error: ok ? null : r?.error ?? "No locations returned",
            detail: ok ? `${inserted} location${inserted === 1 ? "" : "s"}` : undefined,
          },
        ]);
        logger.finish(jid, ok ? `Inserted ${inserted}` : "Failed");
        return r;
      } catch (e: any) {
        logger.appendItems(jid, [{ title: videoTitle || videoId, ok: false, error: e?.message ?? "error" }]);
        logger.finish(jid, "Failed");
        throw e;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "video", videoId] });
    },
  });
  return (
    <button
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
      className="inline-flex items-center gap-1.5 bg-ink text-paper text-xs px-3 py-1.5 hover:bg-amber transition-colors disabled:opacity-50"
      title="Run the geolocation LLM again on this video"
    >
      <Sparkles className="size-3.5" />
      {mut.isPending ? "Geolocating…" : hasGeo ? "Re-geolocate" : "Geolocate"}
    </button>
  );
}


function DeleteVideoBlock({ videoId, youtuberId: _y, onDone }: { videoId: string; youtuberId?: string; onDone: () => void }) {
  // Hard delete not implemented server-side; hide is preferred. Placeholder for future.
  void videoId;
  void _y;
  void onDone;
  return null;
}

const METHOD_LABELS: Record<string, string> = {
  youtube_html: "YouTube captions (HTML)",
  youtube_innertube_android: "YouTube captions (Innertube · Android)",
  youtube_innertube_web: "YouTube captions (Innertube · Web)",
  gemini_youtube: "Gemini (Google AI) — transcribes video directly",
  stt_audio: "Audio → speech-to-text",
  manual: "Manual",
};

function RefreshStatsButton({ videoId }: { videoId: string }) {
  const qc = useQueryClient();
  const refreshFn = useServerFn(refreshVideoStats);
  const [busy, setBusy] = useState(false);
  async function run() {
    if (busy) return;
    setBusy(true);
    try {
      const res: any = await refreshFn({ data: { id: videoId } });
      toast.success(
        res?.duration_seconds
          ? `Stats refreshed — duration ${formatDuration(res.duration_seconds)}`
          : "Stats refreshed (YouTube still reports no duration)",
      );
      qc.invalidateQueries({ queryKey: ["admin", "video", videoId] });
    } catch (e: any) {
      toast.error(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      onClick={run}
      disabled={busy}
      title="Refetch duration, views, likes and comments from YouTube"
      className="inline-flex items-center text-ink/40 hover:text-amber-deep disabled:opacity-50"
    >
      <RefreshCw className={`size-3 ${busy ? "animate-spin" : ""}`} />
    </button>
  );
}

function TranscriptPanel({
  videoId,
  youtubeVideoId,
  storedTranscript,
  originalTranscript,
  source,
  transcriptError,
  fetchedAt,
  logger,
}: {
  videoId: string;
  youtubeVideoId: string;
  storedTranscript: string;
  originalTranscript?: string | null;
  source: string | null;
  transcriptError: string | null;
  fetchedAt: string | null;
  logger: JobLogger;
}) {
  const qc = useQueryClient();

  const retryFn = useServerFn(retranscribeVideo);
  const [transcribing, setTranscribing] = useState(false);
  async function runRetry() {
    if (transcribing) return;
    setTranscribing(true);
    const jid = logger.start("TRANSCRIBE");
    try {
      const res: any = await retryFn({ data: { id: videoId, method: "auto" } });
      if (res?.usage) logger.addUsage(jid, res.usage);
      const ok = !!res?.transcript;
      logger.appendItems(jid, [{
        title: youtubeVideoId,
        ok,
        error: ok ? null : res?.error ?? "No transcript retrieved",
        detail: ok ? `via ${METHOD_LABELS[res.source ?? ""] ?? res.source}` : undefined,
      }]);
      logger.finish(jid, ok ? `Transcribed via ${METHOD_LABELS[res.source ?? ""] ?? res.source}` : "Failed");
      qc.invalidateQueries({ queryKey: ["admin", "video", videoId] });
      qc.invalidateQueries({ queryKey: ["admin", "transcript", youtubeVideoId] });
    } catch (e: any) {
      logger.appendItems(jid, [{ title: youtubeVideoId, ok: false, error: e?.message ?? String(e) }]);
      logger.finish(jid, "Failed");
    } finally {
      setTranscribing(false);
    }
  }

  const updFn = useServerFn(updateVideoAdmin);
  const saveMut = useMutation({
    mutationFn: (transcript: string) =>
      updFn({ data: { id: videoId, patch: { transcript } } }),
    onSuccess: () => {
      toast.success("Transcript saved");
      qc.invalidateQueries({ queryKey: ["admin", "video", videoId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [edited, setEdited] = useState<string | null>(null);
  const value = edited ?? storedTranscript;
  const dirty = edited !== null && edited !== storedTranscript;
  const currentLen = value.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-mono-caps text-ink/50 text-xs">
          Transcript {currentLen > 0 ? `· ${currentLen.toLocaleString()} chars` : ""}
          {source && (
            <span className="ml-2 text-ink/40 normal-case">
              ({METHOD_LABELS[source] ?? source}
              {fetchedAt ? `, ${formatRelative(fetchedAt)}` : ""})
            </span>
          )}
        </label>
        <button
          onClick={runRetry}
          disabled={transcribing}
          className="text-mono-caps text-xs text-amber hover:text-amber-deep disabled:opacity-50 inline-flex items-center gap-1"
          title="Fetch transcript from YouTube / Gemini (auto)"
        >
          <Sparkles className="size-3" /> {transcribing ? "Transcribing…" : storedTranscript ? "Retranscribe" : "Transcribe"}
        </button>
      </div>
      <textarea
        value={value}
        onChange={(e) => setEdited(e.target.value)}
        placeholder="No transcript stored — you can paste one manually here."
        className="w-full font-mono text-xs bg-paper px-3 py-2 ring-hairline mt-1 min-h-48 leading-relaxed"
        rows={12}
      />
      {transcriptError && !storedTranscript && (
        <p className="text-destructive text-xs font-mono mt-1">{transcriptError}</p>
      )}
      {originalTranscript && originalTranscript.trim() && (
        <div className="mt-4">
          <label className="text-mono-caps text-ink/50 text-xs">
            Original transcript (auto-translated to English above) ·{" "}
            {originalTranscript.trim().length.toLocaleString()} chars
          </label>
          <textarea
            value={originalTranscript}
            readOnly
            className="w-full font-mono text-xs bg-secondary/40 px-3 py-2 ring-hairline mt-1 leading-relaxed"
            rows={8}
          />
        </div>
      )}
      {dirty && (
        <div className="flex gap-2 mt-2">
          <button
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate(value)}
            className="bg-ink text-paper px-3 py-1.5 text-xs hover:bg-amber transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Save className="size-3" /> {saveMut.isPending ? "Saving…" : "Save transcript"}
          </button>
          <button
            onClick={() => setEdited(null)}
            className="ring-hairline px-3 py-1.5 text-xs hover:bg-secondary"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}



function ViewHistoryPanel({ videoId }: { videoId: string }) {
  const { i18n } = useTranslation();
  const historyFn = useServerFn(getVideoViewHistory);
  const { data: rows, isLoading } = useQuery({
    queryKey: ["admin", "video-history", videoId],
    queryFn: () => historyFn({ data: { id: videoId } }),
  });

  if (isLoading) {
    return (
      <div className="ring-hairline bg-paper p-5 mb-8 text-ink/50 text-sm">
        Loading view history…
      </div>
    );
  }

  const history = (rows ?? []) as Array<{
    id: string;
    recorded_at: string;
    view_count: number | null;
    like_count: number | null;
    comment_count: number | null;
  }>;

  if (history.length === 0) {
    return (
      <div className="ring-hairline bg-paper p-5 mb-8">
        <div className="text-mono-caps text-ink/50 mb-1">View history</div>
        <div className="text-ink/40 text-sm">No snapshots yet. Run “Refresh views” to record one.</div>
      </div>
    );
  }

  const chartData = history.map((h) => ({
    t: new Date(h.recorded_at).getTime(),
    views: h.view_count ?? 0,
    likes: h.like_count ?? 0,
  }));

  const first = history[0];
  const last = history[history.length - 1];
  const deltaViews = (last.view_count ?? 0) - (first.view_count ?? 0);

  // Show newest first in the table.
  const table = [...history].reverse();

  return (
    <div className="ring-hairline bg-paper mb-8">
      <div className="flex items-baseline justify-between px-5 pt-4">
        <div className="text-mono-caps text-ink/50">View history · {history.length} snapshots</div>
        <div className="text-mono-caps text-ink/40 text-xs">
          +{formatCompact(deltaViews)} views since {formatDate(first.recorded_at, i18n.language)}
        </div>
      </div>
      <div className="h-56 px-3 pt-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid stroke="hsl(30 10% 85%)" strokeDasharray="2 4" />
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              tickFormatter={(t) => formatDate(new Date(t).toISOString(), i18n.language)}
              tick={{ fontSize: 10, fill: "hsl(30 10% 40%)" }}
              stroke="hsl(30 10% 60%)"
            />
            <YAxis
              tickFormatter={(v) => formatCompact(v)}
              tick={{ fontSize: 10, fill: "hsl(30 10% 40%)" }}
              stroke="hsl(30 10% 60%)"
              width={50}
            />
            <Tooltip
              labelFormatter={(t) => formatDate(new Date(t as number).toISOString(), i18n.language)}
              formatter={(v: number, name: string) => [formatCompact(v), name === "views" ? "Views" : "Likes"]}
              contentStyle={{ fontSize: 12, background: "hsl(40 30% 97%)", border: "1px solid hsl(30 10% 80%)" }}
            />
            <Line type="monotone" dataKey="views" stroke="hsl(35 85% 45%)" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-y border-ink/10 bg-secondary/40">
            <tr>
              <th className="text-left font-mono uppercase text-[10px] tracking-wider text-ink/50 px-4 py-2">Date</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-wider text-ink/50 px-4 py-2">Views</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-wider text-ink/50 px-4 py-2">Δ Views</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-wider text-ink/50 px-4 py-2">Likes</th>
              <th className="text-right font-mono uppercase text-[10px] tracking-wider text-ink/50 px-4 py-2">Comments</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/8">
            {table.map((h, idx) => {
              const prev = table[idx + 1];
              const delta = prev ? (h.view_count ?? 0) - (prev.view_count ?? 0) : null;
              return (
                <tr key={h.id} className="hover:bg-secondary/40">
                  <td className="px-4 py-1.5 text-ink/70 whitespace-nowrap">
                    {formatDate(h.recorded_at, i18n.language)}
                  </td>
                  <td className="px-4 py-1.5 text-right font-mono text-xs">{formatCompact(h.view_count ?? 0)}</td>
                  <td className={`px-4 py-1.5 text-right font-mono text-xs ${delta && delta > 0 ? "text-emerald-700" : "text-ink/40"}`}>
                    {delta === null ? "—" : delta > 0 ? `+${formatCompact(delta)}` : formatCompact(delta)}
                  </td>
                  <td className="px-4 py-1.5 text-right font-mono text-xs text-ink/60">{formatCompact(h.like_count ?? 0)}</td>
                  <td className="px-4 py-1.5 text-right font-mono text-xs text-ink/60">{formatCompact(h.comment_count ?? 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
